import React, { useState, useRef } from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { ModalV2 } from 'components/Modal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import Tabs from 'components/Tabs';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import DeleteWaiver from './gql/deleteWaiver';
import GetWaiverCompanies from './gql/getWaiverCompanies';
import GetWaivers from './gql/getWaivers';
import { useDownloadWaiverTemplate } from './gql/useDownloadWaiverTemplate';
import WaiverModal from './WaiverModal';

// Delete modal styled components
const DeleteModalContent = styled.div`
  text-align: center;
  padding: 16px 0;
`;

const DeleteIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;

  svg {
    width: 32px;
    height: 32px;
    color: #ef4444;
  }
`;

const DeleteMessage = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#374151')};
  margin: 0;
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  background: #ef4444;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #dc2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#374151')};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#D1D5DB')};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header Section
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Waiver = (props) => {
  const { data, deleteWaiver, companiesData } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentWaiver, setCurrentWaiver] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [waiverToDelete, setWaiverToDelete] = useState(null);
  const [toasterData, setToasterData] = useState(null);
  const [downloadingWaiverId, setDownloadingWaiverId] = useState(null);

  const { downloadWaiverTemplate } = useDownloadWaiverTemplate();

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();
  const previousSearchRef = useRef(input.queryString || '');

  // Filter state - sync with URL query params
  const [filters, setFilters] = useState({
    status: input.status || 'active',
    type: input.type || '',
    company: input.company || '',
  });

  // Get company options from the companiesData query
  const companies = companiesData?.getWaiverCompanies || [];
  const companyOptions = companies.map((company) => ({
    label: company,
    value: company,
  }));

  // Filter configuration (status is handled by the inline toggle, not the filter panel)
  const filterConfig = [
    {
      key: 'type',
      label: 'Type',
      type: 'radio',
      options: [
        { label: 'Waiver', value: 'waiver' },
        { label: 'Agreement', value: 'agreement' },
      ],
    },
    {
      key: 'company',
      label: 'Company',
      type: 'select',
      placeholder: 'All Companies',
      options: companyOptions,
    },
  ];

  const statusTabs = [
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
  ];

  const handleStatusToggle = (status) => {
    const newFilters = { ...filters, status };
    setFilters(newFilters);
    updateQuery({ status, page: 1 });
  };

  const waivers = data?.getAdminWaivers?.results ?? [];
  const totalCount = data?.getAdminWaivers?.count ?? 0;
  const loading = data?.loading ?? false;
  const totalPages = Math.ceil(totalCount / 15);

  const handleCloseModal = () => {
    setCurrentWaiver(null);
    setIsModalOpen(false);
  };

  const handleSearch = (searchTerm) => {
    if (searchTerm !== previousSearchRef.current) {
      previousSearchRef.current = searchTerm;
      updateQuery({ queryString: searchTerm, page: 1 });
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    // Update URL with filter params, reset to page 1
    updateQuery({
      status: newFilters.status || undefined,
      type: newFilters.type || undefined,
      company: newFilters.company || undefined,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    setFilters({ status: filters.status, type: '', company: '' });
    updateQuery({
      type: undefined,
      company: undefined,
      page: 1,
    });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleCreateWaiver = () => {
    setCurrentWaiver(null);
    setIsModalOpen(true);
  };

  const handleEditWaiver = (waiver) => {
    setCurrentWaiver(waiver.id);
    setIsModalOpen(true);
  };

  const handleDownloadWaiver = async (waiver) => {
    if (!waiver.waiver_id) {
      setToasterData({
        title: 'Error',
        message: 'No waiver ID available for download',
        type: 'error',
      });
      return;
    }

    setDownloadingWaiverId(waiver.id);

    try {
      const response = await downloadWaiverTemplate(waiver.waiver_id);

      if (!response || response.errors) {
        setToasterData({
          title: 'Error',
          message: 'Unable to download waiver template',
          type: 'error',
        });
      } else {
        // Open the download URL in a new tab or trigger download
        const url = response.data.downloadWaiverTemplate;
        window.location.assign(url);
      }
    } catch (error) {
      setToasterData({
        title: 'Error',
        message: 'Unable to download waiver template',
        type: 'error',
      });
    } finally {
      setDownloadingWaiverId(null);
    }
  };

  const handleDeleteClick = (waiver) => {
    setWaiverToDelete(waiver);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!waiverToDelete) return;

    try {
      const response = await deleteWaiver(waiverToDelete.id);

      if (!response || response.errors) {
        setToasterData({
          title: 'Error',
          message: 'Error Deleting Waiver',
          type: 'error',
        });
      } else {
        setToasterData({
          title: 'Success!',
          message: 'Waiver Successfully Deleted',
          type: 'success',
        });
      }
    } catch (error) {
      setToasterData({
        title: 'Error',
        message: 'Error Deleting Waiver',
        type: 'error',
      });
    } finally {
      setDeleteModalVisible(false);
      setWaiverToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setWaiverToDelete(null);
  };

  // Define table columns
  const columns = [
    {
      field: 'name',
      header: 'Waiver Name',
    },
    {
      field: 'waiver_id',
      header: 'Waiver ID',
    },
    {
      field: 'company',
      header: 'Company',
      render: (row) => row.company || '—',
    },
    {
      field: 'type',
      header: 'Type',
      render: (row) => {
        const typeValue = row.type?.toLowerCase();
        const isAgreement = typeValue === 'agreement';
        return (
          <Badge variant={isAgreement ? 'info' : 'default'} size="small">
            {isAgreement ? 'Agreement' : 'Waiver'}
          </Badge>
        );
      },
    },
    {
      field: 'status',
      header: 'Status',
      render: (row) => {
        // Treat null/undefined as active
        const isActive = !row.status || row.status === 'active';
        return (
          <Badge variant={isActive ? 'success' : 'error'} size="small">
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'download',
      title: 'Download',
      onClick: handleDownloadWaiver,
      disabled: (waiver) =>
        !waiver.waiver_id || downloadingWaiverId === waiver.id,
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditWaiver,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  if (!data?.getAdminWaivers) return null;

  return (
    <EnhancedThemeProvider>
      <div style={{ background: 'transparent', minHeight: '100vh' }}>
        <PageContainer>
          {/* Header Card with Icon and Title */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <PageHeaderIcon icon="waivers" />
              <HeaderTitle>Waivers</HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Main Content Card */}
          <Card noPadding>
            <ContentWrapper>
              <PageHeader>
                <SearchInput
                  onSearch={handleSearch}
                  placeholder="Search waivers by name..."
                />
                <Tabs
                  tabs={statusTabs}
                  activeTab={filters.status}
                  onTabChange={handleStatusToggle}
                  size="small"
                />
                <TableFilter
                  filters={filterConfig}
                  values={filters}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                />
                <ButtonV2
                  onClick={handleCreateWaiver}
                  style={{ width: 'auto', flexShrink: 0 }}
                >
                  Create Waiver
                </ButtonV2>
              </PageHeader>

              <DataTable
                columns={columns}
                data={waivers}
                loading={loading}
                keyField="id"
                emptyMessage="No waivers found"
                emptySubtext="Try adjusting your search terms or create a new waiver"
                emptyIcon={
                  <svg
                    width="48"
                    height="48"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                }
                actions={actions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </ContentWrapper>
          </Card>
        </PageContainer>
      </div>

      {/* Waiver Modal */}
      <WaiverModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        currentWaiver={currentWaiver}
        onSuccess={(message) =>
          setToasterData({
            title: 'Success!',
            message,
            type: 'success',
          })
        }
        onError={(message) =>
          setToasterData({
            title: 'Error',
            message,
            type: 'error',
          })
        }
      />

      {/* Delete Confirmation Modal */}
      <ModalV2
        isOpen={deleteModalVisible}
        onClose={handleDeleteCancel}
        title="Delete Waiver"
        maxWidth="400px"
        footer={
          <>
            <CancelButton onClick={handleDeleteCancel}>Cancel</CancelButton>
            <DeleteButton onClick={handleDeleteConfirm}>Delete</DeleteButton>
          </>
        }
      >
        <DeleteModalContent>
          <DeleteIcon>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </DeleteIcon>
          <DeleteMessage>
            Are you sure you want to delete &quot;{waiverToDelete?.name}&quot;?
            This action cannot be undone.
          </DeleteMessage>
        </DeleteModalContent>
      </ModalV2>

      {/* Success/Error Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={5000}
        />
      )}
    </EnhancedThemeProvider>
  );
};

export default compose(GetWaivers, GetWaiverCompanies, DeleteWaiver)(Waiver);
